import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe2, Languages, MapPin, ExternalLink } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { LANGUAGES, EXCERPTS, AMAZON_STORE } from "@/data/books";
import { trackClick } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const STATS = [
  { icon: Languages, value: "6", label: "Languages at launch" },
  { icon: MapPin, value: "7+", label: "Amazon marketplaces" },
  { icon: Globe2, value: "4", label: "Continents reading along" },
];

export default function GlobalReach() {
  const [active, setActive] = useState("EN");
  const lang = LANGUAGES.find((l) => l.code === active);

  return (
    <section
      id="global"
      data-testid="global-reach-section"
      className="grain relative overflow-hidden bg-emerald py-24 lg:py-32"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(1000px 560px at 50% 118%, rgba(19,78,61,0.85), transparent 70%)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          dark
          testId="global-heading"
          eyebrow="Global Reach"
          title={
            <>
              One Forest, <em className="text-gold">Six Tongues.</em>
            </>
          }
          sub="Every Banyan Tree Press title is released simultaneously across US, UK, European, and Latin American Amazon marketplaces — nurturing bilingual homes and cultural literacy from the very first page."
        />

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
          className="mt-14"
        >
          <div
            className="flex flex-wrap gap-2"
            role="tablist"
            aria-label="Preview editions by language"
            data-testid="global-language-tabs"
          >
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                role="tab"
                aria-selected={active === l.code}
                data-testid={`global-tab-${l.code.toLowerCase()}`}
                onClick={() => setActive(l.code)}
                className={`flex items-center gap-2 rounded-full border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] transition-all duration-300 ${
                  active === l.code
                    ? "border-gold bg-gold text-emerald-ink shadow-glow"
                    : "border-cream/20 text-cream/70 hover:border-gold/50 hover:text-gold"
                }`}
              >
                <span aria-hidden="true">{l.flag}</span>
                {l.name}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.figure
              key={active}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="mt-8 border border-gold/25 bg-emerald-deep/70 p-8 sm:p-12"
              data-testid="global-excerpt-panel"
            >
              <blockquote className="mx-auto max-w-3xl text-center font-serif text-xl italic leading-relaxed text-cream/90 sm:text-2xl">
                “{EXCERPTS[active]}”
              </blockquote>
              <figcaption className="mt-7 flex flex-col items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold/80">
                  The {lang.name} edition · {lang.market}
                </span>
                <a
                  href={AMAZON_STORE}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="global-edition-link"
                  onClick={() => trackClick(`edition-${active.toLowerCase()}`, AMAZON_STORE, "global")}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-gold underline-offset-4 transition-colors hover:text-gold-amber hover:underline"
                >
                  Browse the {lang.name} editions
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </motion.div>

        <div className="mt-14 grid gap-6 sm:grid-cols-3">
          {STATS.map(({ icon: Icon, value, label }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
              className="flex items-center gap-5 border border-gold/15 bg-emerald-deep/50 px-7 py-6"
              data-testid={`global-stat-${i + 1}`}
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/35 text-gold">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="font-serif text-3xl font-semibold text-cream">{value}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60">
                  {label}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
